// Benchmark Entries Page
// Dedicated page for managing benchmark entries (detailed cost data)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { DynamicForm } from '@/components/common/DynamicForm';
import { createBenchmarkEntryEntity } from '@/config/entities/benchmarkEntriesEnhanced';
import { benchmarkEntriesAPI } from '@/services/benchmarkEntriesAPI';
import { benchmarkService } from '@/services/benchmarksCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { 
  ArrowLeftIcon,
  DocumentArrowUpIcon, 
  DocumentArrowDownIcon,
  // PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

export const BenchmarkEntriesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [benchmark, setBenchmark] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [entriesRefreshKey, setEntriesRefreshKey] = useState(0);

  const entryEntity = createBenchmarkEntryEntity();
  const entriesService = React.useMemo(() => (id ? {
    getAll: (params?: { page?: number; limit?: number; search?: string }) =>
      benchmarkEntriesAPI.getList(id, params),
    create: (data: any) => benchmarkEntriesAPI.create(id, data),
    update: (entryId: string, data: any) => benchmarkEntriesAPI.update(entryId, data),
    delete: (entryId: string) => benchmarkEntriesAPI.delete(entryId)
  } : null), [id]);

  useEffect(() => {
    if (id) {
      loadBenchmark();
    }
  }, [id]);

  const loadBenchmark = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const benchmarkData = await benchmarkService.getById(id);
      setBenchmark(benchmarkData);
    } catch (error) {
      console.error('Error loading benchmark:', error);
      setBenchmark(null);
    } finally {
      setLoading(false);
    }
  };


  const handleView = (_item: any) => {
    // Navigate to entry details if needed
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleCreateSubmit = async (data: any) => {
    if (!id) return;
    try {
      await benchmarkEntriesAPI.create(id, data);
      setShowCreateModal(false);
      setEntriesRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error creating entry:', error);
    }
  };

  const handleEditSubmit = async (data: any) => {
    try {
      await benchmarkEntriesAPI.update(editingItem.id, data);
      setShowEditModal(false);
      setEditingItem(null);
      setEntriesRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleExport = async () => {
    try {
      // Implement export functionality
          } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const handleImport = async () => {
    try {
      // Implement import functionality
          } catch (error) {
      console.error('Error importing data:', error);
    }
  };

  const customActions = [
    {
      label: 'View',
      icon: EyeIcon,
      onClick: handleView,
      variant: 'primary' as const
    },
    {
      label: 'Edit',
      icon: PencilIcon,
      onClick: handleEdit,
      variant: 'secondary' as const
    },
    {
      label: 'Delete',
      icon: TrashIcon,
      onClick: async (item: any) => {
        if (window.confirm('Are you sure you want to delete this entry?')) {
          await benchmarkEntriesAPI.delete(item.id);
          setEntriesRefreshKey((k) => k + 1);
        }
      },
      variant: 'danger' as const
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!benchmark) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('benchmarking.benchmarkNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="benchmark-entries-page">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/benchmarking/${id}`)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('common.back')}
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('benchmarking.entries')}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {t('benchmarking.entriesDescription')} - {benchmark.name}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExport}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {t('benchmarking.exportData')}
              </button>
              <button
                onClick={handleImport}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                {t('benchmarking.importData')}
              </button>
              <button
                onClick={() => navigate(`/benchmarking/${id}/analytics`)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                {t('benchmarking.analytics')}
              </button>
            </div>
          </div>

          {/* Benchmark Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('benchmarking.benchmarkInfo')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('benchmarking.name')}</label>
                <p className="text-sm text-gray-900">{benchmark.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('benchmarking.year')}</label>
                <p className="text-sm text-gray-900">{benchmark.benchmark_year}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('benchmarking.status')}</label>
                <p className="text-sm text-gray-900">{benchmark.status}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('benchmarking.currency')}</label>
                <p className="text-sm text-gray-900">{benchmark.currency}</p>
              </div>
            </div>
          </div>

          {/* Entries Table */}
          <div className="bg-white shadow rounded-lg">
            {entriesService && (
              <FlexibleCRUDTable
                key={`entries-${id}-${entriesRefreshKey}`}
                entity={entryEntity}
                service={entriesService}
                onRowClick={handleView}
                onEdit={handleEdit}
                searchPlaceholder={t('benchmarking.searchEntries')}
                showCreateButton={true}
                showSearch={true}
                showPagination={true}
                pageSize={10}
                customActions={customActions}
                className="w-full"
              />
            )}
          </div>
        </div>
      </div>

      {/* Create Entry Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('benchmarking.addEntry')}
              </h3>
              <DynamicForm
                entity={entryEntity}
                onSubmit={handleCreateSubmit}
                onCancel={handleCancel}
                submitLabel={t('common.create')}
                loading={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('benchmarking.editEntry')}
              </h3>
              <DynamicForm
                entity={entryEntity}
                initialData={editingItem}
                onSubmit={handleEditSubmit}
                onCancel={handleCancel}
                submitLabel={t('common.update')}
                loading={false}
                isEditing={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
